import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import logo from "../../assets/images/logo.png";
import dp from "../../assets/images/profilepicture.png";

// Icons for Google Adsense-style sidebar
import {
  HomeIcon,
  ChartBarIcon,
  DocumentTextIcon,
  WrenchIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  UserGroupIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

const Sidebar: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // Google Adsense exact structure
  const navigation = [
    {
      section: "Main",
      items: [
        { name: "Home", to: "/", icon: HomeIcon },
        { name: "My ads", to: "/ad-units", icon: ChartBarIcon },
        { name: "Allow & block ads", to: "/blocking", icon: WrenchIcon },
        {
          name: "Performance reports",
          to: "/performance",
          icon: DocumentTextIcon,
        },
        { name: "Optimization", to: "/optimization", icon: ArrowPathIcon },
      ],
    },
    {
      section: "Admin", // Only shown for admin users
      items: [
        { name: "Users", to: "/admin/users", icon: UserGroupIcon },
        {
          name: "Data generation",
          to: "/admin/data-generation",
          icon: ChartBarIcon,
        },
        { name: "Notifications", to: "/admin/notifications", icon: BellIcon },
      ],
      condition: user?.role === "admin",
    },
    {
      section: "Settings & Help",
      items: [
        { name: "Settings", to: "/settings", icon: CogIcon },
        { name: "Help", to: "/help", icon: QuestionMarkCircleIcon },
        {
          name: "Send feedback",
          to: "/feedback",
          icon: ChatBubbleLeftRightIcon,
        },
      ],
    },
  ];

  return (
    <div className="w-64 bg-white min-h-screen flex flex-col flex-shrink-0 border-r border-gray-200 fixed top-0">
      {/* Logo - Google Adsense Style (no text) */}
      <div className="h-16 border-b border-gray-200 px-4 flex items-center">
        <img src={logo} alt="AdSense" className="h-28 w-auto" />
      </div>

      {/* Navigation */}
      <div className="flex-1 py-4">
        {navigation.map((section, sectionIndex) => {
          // Skip admin section if user is not admin
          if (section.condition === false) return null;

          return (
            <div key={sectionIndex} className="mb-6">
              {/* Section divider */}
              {sectionIndex > 0 && (
                <div className="h-px bg-gray-200 mx-4 mb-4"></div>
              )}

              {/* Section items */}
              <div className="">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.to;
                  const Icon = item.icon;

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={`flex items-center px-4 py-3 text-sm rounded-e-3xl mr-2 transition-colors border-l-4 ${
                        isActive
                          ? "bg-[#1967d2]/10 text-[#1967d2] font-medium border-[#1967d2]"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 mr-3 ${
                          isActive ? "text-blue-600" : "text-gray-500"
                        }`}
                      />
                      <span className="">{item.name}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* User profile at bottom - Google Style */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center space-x-3">
          <img src={dp} alt="" className="w-8 h-8 rounded-full" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
